// https://chatgpt.com/c/691bce0d-bbcc-8325-be2b-bf95486b31b2
"use client";

import * as React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import CatalogItemSnippet from "@/components/common/catalog-item-snippet";
import CategoryCarouselMobile from "@/components/common/category-carousel-mobile";

interface CategoryCarouselProps {
  title: string;
  seeAllHref?: string | null;
  products: any[];
  slidesToScroll?: number; // e.g. 5
}

export default function CategoryCarousel({
  title,
  seeAllHref = "#",
  products,
  slidesToScroll = 3,
}: CategoryCarouselProps) {
  const seeAllCard = (
    !!seeAllHref && (
    <Link
      href={seeAllHref}
      className="flex flex-col items-center justify-center group"
      tabIndex={-1}
      onClick={e => e.stopPropagation()}
    >
      <Button
        size="icon"
        className="pointer-events-auto h-14 w-14 rounded-full flex items-center justify-center hover:bg-teal-500 bg-teal-400 group-hover:bg-teal-500 cursor-pointer shadow-md transition group-hover:scale-105"
        tabIndex={-1}
        asChild
      >
        <span>
          <ChevronRight className="h-12 w-12" />
        </span>
      </Button>
      <span className="text-medium font-semibold text-teal-500 hover:text-teal-600 group-hover:text-teal-600 py-3 cursor-pointer transition-colors">
        Ver todos
      </span>
    </Link>)
  );

  return (
    <section className="w-full">
      {/* Mobile */}
      <div className="md:hidden">
        <CategoryCarouselMobile
          title={title}
          seeAllHref={seeAllHref}
          products={products}
        />
      </div>

      {/* Desktop: Carousel */}
      <div className="hidden md:block">
        <Carousel
          opts={{
            align: "start",
            slidesToScroll,
          }}
          className="w-full"
        >
          <div className="flex items-center justify-between gap-4 mb-3">
            <h2 className="text-xl font-semibold text-foreground tracking-tight">{title}</h2>
            <div className="ml-auto flex items-center gap-3">
              {!!seeAllHref && (
                <Link
                  href={seeAllHref}
                  className="text-medium font-medium text-teal-500 hover:text-teal-600 cursor-pointer"
                >
                  Ver todos
                </Link>
              )}
              <div className="flex items-center gap-2">
                <CarouselPrevious
                  className="
                    static top-auto left-auto
                    translate-y-0
                    h-8 w-8 rounded-full border border-input
                    bg-background shadow-sm
                    flex items-center justify-center
                    disabled:opacity-40
                  "
                />
                <CarouselNext
                  className="
                    static top-auto left-auto
                    translate-y-0
                    h-8 w-8 rounded-full border border-input
                    bg-background shadow-sm
                    flex items-center justify-center
                    disabled:opacity-40
                  "
                />
              </div>
            </div>
          </div>

          <CarouselContent className="-ml-4">
            {products.map((product) => (
              <CarouselItem
                key={product.id}
                className="pl-4 basis-1/4 lg:basis-1/6 xl:basis-1/7 2xl:basis-1/8"
              >
                <CatalogItemSnippet item={product} />
              </CarouselItem>
            ))}
            <CarouselItem
              className="pl-4 flex flex-col items-center justify-center basis-1/4 lg:basis-1/6 xl:basis-1/7 2xl:basis-1/8"
            >
              {seeAllCard}
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
}
